import { SeoLandingLayout, SeoLandingPageData } from '../../components/SeoLandingLayout';

const data: SeoLandingPageData = {
  seo: {
    title: 'Gestion de trésorerie TPE : survivre et grandir sans manquer de cash | Trezoone',
    description: 'La gestion de trésorerie d\'une TPE, c\'est souvent une affaire de semaines. Apprenez à anticiper les tensions, gérer votre BFR et dormir la nuit.',
    canonical: '/gestion-tresorerie-tpe',
    keywords: 'gestion tresorerie tpe, trésorerie très petite entreprise, pilotage tresorerie tpe, cash flow tpe, tresorerie autoentrepreneur',
  },
  hero: {
    tag: 'Trésorerie TPE',
    title: 'En TPE, une mauvaise semaine de trésorerie peut tout changer.',
    subtitle: 'Les très petites entreprises ont peu de marge d\'erreur. Chaque retard de paiement, chaque charge imprévue pèse lourd. Voici comment reprendre le contrôle.',
    ctaLabel: 'Tester Trezoone',
  },
  pilierLink: {
    label: 'Guide complet : gestion de trésorerie',
    href: '/gestion-tresorerie',
  },
  sections: [
    {
      type: 'hook',
      title: 'La trésorerie TPE, c\'est du concret',
      content: 'En TPE, vous n\'avez pas de DAF, pas de contrôleur de gestion. C\'est vous. Et votre banque. Et votre Excel approximatif.',
      items: [
        'Un client qui paie en retard peut bloquer le paiement de vos charges fixes',
        'La TVA trimestrielle tombe le 15, et vous l\'avez oubliée dans vos prévisions',
        'Vous ne savez pas exactement combien de mois vous pouvez tenir si l\'activité ralentit',
      ],
    },
    {
      type: 'explain',
      title: 'Les 4 piliers de la trésorerie TPE',
      content: 'Pour une TPE, bien gérer sa trésorerie repose sur 4 fondamentaux :',
      items: [
        '1. Solde en temps réel : savoir exactement ce qu\'il y a sur le compte à tout moment',
        '2. Prévision à 3 mois : anticiper les charges fixes et les recettes attendues',
        '3. Gestion des délais clients : relancer dès le premier jour de retard',
        '4. Réserve de trésorerie : conserver l\'équivalent de 2 mois de charges en permanence',
      ],
    },
    {
      type: 'example',
      title: 'Exemple : consultant indépendant, 3 clients réguliers',
      content: 'Situation : CA mensuel 8 000 €, charges mensuelles 4 500 €, 2 clients sur 3 paient à 30-45 jours.',
      items: [
        'Janvier : factures client A et B (6 000 €) → encaissement fin février',
        'Charges janvier : 4 500 € → solde fin janvier -4 500 € si réserve insuffisante',
        'Avec Trezoone : le creux de janvier est prévu en décembre → acompte demandé, réserve constituée',
        'Résultat : zéro découvert, relation client préservée, pas de stress',
      ],
    },
    {
      type: 'midcta',
      title: 'Un consultant indépendant a éliminé ses découvertes en 30 jours',
      content: 'Essai gratuit 14 jours · Simple · 59 €/mois — moins cher qu\'un découvert',
    },
    {
      type: 'solution',
      title: 'Trezoone s\'adapte aux TPE',
      content: 'Pas besoin d\'un outil complexe. Trezoone est simple, rapide à prendre en main, et conçu pour les réalités d\'une TPE.',
      items: [
        'Interface épurée, opérationnelle en 30 minutes',
        'Import CSV/OFX depuis votre banque, sans technique',
        'Plan de trésorerie sur 12 mois avec alertes automatiques',
        'Indicateur : "combien de semaines de charges vous reste-t-il ?"',
        'Rappels automatiques avant les échéances importantes (TVA, cotisations…)',
        'À partir de 59 €/mois — moins qu\'une heure d\'expert-comptable',
      ],
    },
    {
      type: 'faq',
      title: 'Questions fréquentes',
      faq: [
        {
          q: 'Un auto-entrepreneur a-t-il besoin d\'un logiciel de trésorerie ?',
          a: 'Oui, dès que votre CA dépasse 2 000 €/mois. Le risque de décalage entre facturation et encaissement est réel, même pour un indépendant. Et les erreurs coûtent cher.',
        },
        {
          q: 'Trezoone convient-il si je n\'ai pas de connaissances financières ?',
          a: 'C\'est précisément pour ça qu\'il a été conçu. Interface simple, langage clair, pas de jargon comptable. Si vous savez lire votre relevé de compte, vous pouvez utiliser Trezoone.',
        },
        {
          q: 'Combien de temps faut-il pour mettre en place Trezoone en TPE ?',
          a: 'Comptez 30 à 60 minutes pour importer vos données, configurer vos catégories et avoir votre premier plan de trésorerie. Ensuite, 15 minutes par mois pour maintenir à jour.',
        },
      ],
    },
    {
      type: 'cluster',
      title: 'Trésorerie TPE, freelance et indépendants',
      links: [
        { label: 'Gestion trésorerie PME', href: '/gestion-tresorerie-pme' },
        { label: 'Plan de trésorerie', href: '/plan-tresorerie' },
        { label: 'Logiciel trésorerie entreprise', href: '/logiciel-tresorerie-entreprise' },
        { label: 'Prévision de trésorerie', href: '/prevision-tresorerie' },
        { label: 'Trésorerie freelance', href: '/tresorerie-freelance-independant' },
        { label: 'Trésorerie startup', href: '/tresorerie-startup' },
        { label: 'Manque de trésorerie', href: '/manque-tresorerie' },
        { label: 'Problème cash flow', href: '/probleme-cash-flow' },
      ],
    },
    {
      type: 'cta',
      title: 'Prenez le contrôle de votre trésorerie TPE',
      content: 'Essai gratuit 14 jours. Interface simple. Résultats concrets.',
    },
  ],
  relatedLinks: [
    { label: 'Gestion trésorerie PME', href: '/gestion-tresorerie-pme' },
    { label: 'Logiciel trésorerie', href: '/logiciel-tresorerie' },
    { label: 'Plan de trésorerie', href: '/plan-tresorerie' },
    { label: 'Simulateur trésorerie', href: '/simulateur-tresorerie-association' },
  ],
};

interface Props { onNavigate?: (p: string) => void; onSignIn?: () => void; onSignUp?: () => void; }

export default function GestionTresorerieTPEPage({ onNavigate, onSignIn, onSignUp }: Props) {
  return <SeoLandingLayout data={data} onNavigate={onNavigate} onSignIn={onSignIn} onSignUp={onSignUp} />;
}
